import { useRef, useState, useEffect, MutableRefObject, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CellClickedEvent, ColDef, GridApi, ICellRendererParams } from 'ag-grid-community';
import SVG from 'react-inlinesvg';
import { cancelFunctions } from 'utils/cancelFunctions';
import { fetchCreativeSetTable } from 'modules/api/creativeSets';
import { CREATIVE_STATUS_BADGE } from 'consts/creative';
import { usePageFilters } from 'customHooks/usePageFilters';
import { CreativeSetsFilters } from 'store/pages/contentManagement/creativeSets/reducer';
import withLoader from 'components/hocs/withLoader';
import Table from 'lib/Table/Table';
import { TableHeight, TableRowHeight } from 'lib/Table/Table.types';
import StatusRenderer from 'components/common/StatusRenderer';
import FrameDetailsRenderer from 'components/common/FrameDetailsRenderer';
import FramesRenderer from 'components/common/FramesRenderer';
import Button, { Size, Color } from 'lib/Button';
import emptyIcon from 'assets/icons/empty.svg';
import { CreativeSetPanel } from 'components/common/types/Creative.types';
import { transformCreativeSetsData } from 'components/pages/ContentManagement/transformRequestData';
import { isValidIndex } from 'utils/numberUtils';
import CopyToClipboardCellRenderer from 'components/common/CopyToClipboardCellRenderer';
import { useNavigateTable } from 'components/pages/ContentManagement/Creatives/Content/Creatives/useNavigateTable';
import { normalizePageIndex } from 'components/pages/ContentManagement/Creatives/Content/Creatives/utils';
import CreativesRenderer from './CreativesRenderer';
import './CreativeSetsTable.pcss';

const pageSize = 20;

const statusColumnDef: ColDef[] = [
  {
    headerName: 'Status',
    field: 'status',
    maxWidth: 200,
    minWidth: 200,
    headerTooltip: 'Status',
    cellRenderer: StatusRenderer,
    cellRendererParams: { statuses: CREATIVE_STATUS_BADGE },
    colId: 'status',
    pinned: 'left',
    cellStyle: {
      cursor: 'pointer',
    },
  },
];

const creativeSetColumnDef: ColDef[] = [
  {
    headerName: 'No. VU',
    field: 'visualUnitIds',
    maxWidth: 90,
    minWidth: 90,
    headerTooltip: 'No. VU',
    cellRenderer: FrameDetailsRenderer,
    colId: 'visualUnitIds',
    pinned: 'left',
    cellStyle: {
      cursor: 'pointer',
    },
  },
  {
    headerName: 'No. frames',
    field: 'panels',
    minWidth: 150,
    headerTooltip: 'No. frames',
    cellRenderer: FramesRenderer,
    colId: 'panels',
    pinned: 'left',
    cellStyle: {
      cursor: 'pointer',
    },
  },
  {
    headerName: 'Size (WxH)',
    field: 'sizes',
    minWidth: 150,
    headerTooltip: 'Size',
    cellRenderer: (params: ICellRendererParams) =>
      params.data?.panels
        ? params.data.panels.map((item: CreativeSetPanel) => `${item.width}x${item.height}`).join(', ')
        : '',
    cellStyle: {
      cursor: 'pointer',
    },
  },
  {
    headerName: 'DSP',
    field: 'dsp',
    minWidth: 150,
    headerTooltip: 'DSP',
    cellRenderer: (params: ICellRendererParams) => params.data?.dsp.name || '',
    colId: 'dsp',
    cellStyle: {
      cursor: 'pointer',
    },
  },
  {
    headerName: 'Advertiser',
    field: 'advertiser',
    minWidth: 150,
    headerTooltip: 'Advertiser',
    cellRenderer: (params: ICellRendererParams) => (
      <CopyToClipboardCellRenderer value={params.data?.advertiser.name || ''} />
    ),
    colId: 'advertiser',
    cellStyle: {
      cursor: 'pointer',
    },
  },
  {
    headerName: 'Brand',
    field: 'brand',
    minWidth: 150,
    headerTooltip: 'Brand',
    cellRenderer: (params: ICellRendererParams) => (
      <CopyToClipboardCellRenderer value={params.data?.brand.name || ''} />
    ),
    colId: 'brand',
    cellStyle: {
      cursor: 'pointer',
    },
  },
  {
    headerName: 'Creative set code',
    field: 'code',
    minWidth: 150,
    headerTooltip: 'Creative set code',
    colId: 'code',
    resizable: false,
    cellRenderer: (params: ICellRendererParams) => <CopyToClipboardCellRenderer value={params.data?.code || ''} />,
    cellStyle: {
      cursor: 'pointer',
    },
  },
];

// eslint-disable-next-line sonarjs/cognitive-complexity
const CreativeSetTable = (): React.ReactElement => {
  const navigate = useNavigate();
  const tableApiRef: MutableRefObject<GridApi | null> = useRef(null);
  const { filters } = usePageFilters<CreativeSetsFilters>();
  const [isEmptyRows, setIsEmptyRows] = useState(true);

  const { creativeSetCode, dsp, advertiser, brand, visualUnitCode } = filters;

  const fetchCreativesTable = useCallback(
    async ({ pageIndex }) => {
      try {
        const { content, totalElements } = await fetchCreativeSetTable(cancelFunctions, {
          ...(creativeSetCode ? { creativeSetCode } : {}),
          ...(dsp ? { dspCode: dsp.code } : {}),
          ...(advertiser ? { advertiserCode: advertiser.code } : {}),
          ...(brand ? { brandCode: brand.code } : {}),
          ...(visualUnitCode ? { visualUnitCode } : {}),
          page: pageIndex,
          size: pageSize,
        });
        const transformedContent = transformCreativeSetsData(content);

        if (totalElements === 0) {
          setIsEmptyRows(true);
        } else {
          setIsEmptyRows(false);
        }
        return { content: transformedContent, totalElements };
      } catch {
        return { content: [], totalElements: 0 };
      }
    },
    [cancelFunctions, creativeSetCode, dsp, advertiser, brand, visualUnitCode],
  );

  const { refreshTable, pageIndex, wirePaginationToSearchParams } = useNavigateTable({
    tableApiRef,
    pageSize,
    pageParamKey: 'creativeSetPage',
    fetchCreativesTable,
  });

  const creativesColumnDef: ColDef[] = [
    {
      headerName: 'Creatives',
      field: 'creatives',
      minWidth: 190,
      headerTooltip: 'Creatives',
      cellRenderer: (params: ICellRendererParams) =>
        CreativesRenderer({
          data: params?.data,
          creativeSetPage: isValidIndex(pageIndex) ? pageIndex : undefined,
        }),
      colId: 'creatives',
      pinned: 'left',
    },
  ];

  const onCellClicked = (params: CellClickedEvent): void => {
    if (!params.data || params.column.getColId() === 'creatives') return;

    const {
      event,
      data: { id },
    } = params;

    if (event?.defaultPrevented) return;

    event?.preventDefault?.();
    navigate(
      `/content-management/creative-set-details/${id}`,
      isValidIndex(pageIndex) ? { state: { creativeSetPage: normalizePageIndex(pageIndex) } } : undefined,
    );
  };

  const getTableApi = (tableApi: GridApi): void => {
    tableApiRef.current = tableApi;
    wirePaginationToSearchParams(tableApi);
    refreshTable();
  };

  useEffect(() => {
    refreshTable();
  }, [filters]);

  return (
    <>
      <div className={isEmptyRows ? 'hidden' : 'block'}>
        <Table
          columnDefs={[...statusColumnDef, ...creativesColumnDef, ...creativeSetColumnDef]}
          getTableApi={getTableApi}
          syncPageIndex={pageIndex}
          onCellClicked={onCellClicked}
          extraGridOptions={{
            pagination: true,
            paginationPageSize: pageSize,
            cacheBlockSize: pageSize,
            maxBlocksInCache: 1,
            enableCellTextSelection: true,
            domLayout: 'autoHeight',
            rowModelType: 'infinite',
            rowHeight: TableRowHeight.NORMAL,
            rowSelection: 'single',
          }}
          tableHeight={TableHeight.AUTO}
          dataTestId="creative-set-table"
          className="creative-set-table ag-theme-alpine table-height--normal"
        />
      </div>
      {isEmptyRows && (
        <div className="flex flex-col justify-center items-center min-h-screen bg-neutral-50 border rounded-lg border-neutral-100-opacity-10 text-neutral-950-opacity-60">
          <SVG src={emptyIcon} />
          <p className="header-lg mt-11 mb-4">No creative sets found</p>
          <p className="mb-4 text-center body-base">
            You have not created any set yet. Please, start your first <br /> creative set to assign to your visual
            units
          </p>
          <Button
            onClick={() => navigate('/content-management/creative-set-details')}
            size={Size.LARGE}
            color={Color.TERTIARY}
            label="New create set"
          />
        </div>
      )}
    </>
  );
};

export default withLoader(CreativeSetTable);
